import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { EASE } from './Reveal';

const LINKS = [
  { to: '/', label: 'Inicio', id: 'inicio' },
  { to: '/seminarios', label: 'Seminarios', id: 'seminarios' },
  { to: '/aval-internacional', label: 'Aval Internacional', id: 'aval' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-navy/85 backdrop-blur-xl border-b border-white/10 py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-logo" className="relative z-50 flex items-center gap-3">
            <img src="/images/logo-white.png" alt="Future Leaders" className="h-9 w-auto sm:h-10" />
          </Link>

          <nav className="hidden items-center gap-9 md:flex" aria-label="Navegación principal">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `group relative font-mono text-[11px] font-bold uppercase tracking-[0.25em] transition-colors duration-300 ${
                    isActive ? 'text-gold' : 'text-white/70 hover:text-white'
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-crimson transition-all duration-300 group-hover:w-full" />
              </NavLink>
            ))}
            <Link
              to="/seminarios"
              data-testid="nav-cta-programa"
              className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-crimson-hover"
            >
              El Programa <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="relative z-50 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white md:hidden"
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-navy-deep/98 backdrop-blur-2xl px-8 md:hidden"
          >
            <div className="space-y-2">
              {LINKS.map((l, i) => (
                <div key={l.id} className="overflow-hidden">
                  <motion.div
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '110%' }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.08 + i * 0.08 }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.id}`}
                      className={({ isActive }) =>
                        `block font-display text-4xl font-extrabold uppercase tracking-tight ${
                          isActive ? 'text-crimson' : 'text-white'
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                </div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-12 font-mono text-[11px] uppercase tracking-[0.3em] text-white/40"
            >
              Jóvenes 14–18 años · Aval internacional EE.UU.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
